import {
  ActionDefinition,
  FormAction,
  AutomationAction,
  CreateAutomationActionRequest,
  DynamicField,
  ActionValidation,
  DynamicFieldValidation,
  ActionSearchFilters,
} from '../types/actions';

// Função para validar ações
export const validateActions = (actions: FormAction[]): ActionValidation => {
  const errors: string[] = [];

  // Se não há ações, é válido
  if (actions.length === 0) {
    return { isValid: true, errors: [] };
  }

  actions.forEach((action, index) => {
    if (!action.actionKey) {
      errors.push(`Ação ${index + 1}: Tipo de ação é obrigatório`);
    }
    if (!action.actionLabel) {
      errors.push(`Ação ${index + 1}: Nome da ação é obrigatório`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Função para validar campos dinâmicos de uma ação
export const validateActionDynamicFields = (
  action: FormAction,
  actionDefinition: ActionDefinition
): DynamicFieldValidation => {
  const errors: string[] = [];

  if (!actionDefinition?.config_schema) {
    return { isValid: true, errors: [] };
  }

  const dynamicFields = generateDynamicFields(actionDefinition);

  dynamicFields.forEach(field => {
    const value = action.dynamicFields?.[field.key];

    if (field.required && (!value || value === '')) {
      errors.push(`${field.label} é obrigatório`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Função para gerar campos dinâmicos baseados no config_schema
export const generateDynamicFields = (actionDefinition: ActionDefinition): DynamicField[] => {
  console.log('=== DEBUG: generateDynamicFields ===');
  console.log('Action Definition:', actionDefinition);
  console.log('Config Schema:', actionDefinition.config_schema);

  const fields: DynamicField[] = [];

  if (!actionDefinition?.config_schema) {
    console.log('❌ Config Schema inválido ou nulo');
    return fields;
  }

  // Verificar se as propriedades estão no nível raiz ou dentro de 'properties'
  const propertiesToProcess =
    actionDefinition.config_schema.properties || actionDefinition.config_schema;

  console.log('🔍 Properties encontradas:', Object.keys(propertiesToProcess));

  Object.entries(propertiesToProcess).forEach(([key, schema]: [string, any]) => {
    // Pular propriedades que não são campos de configuração ou são campos internos
    if (
      key === 'properties' ||
      typeof schema !== 'object' ||
      schema === null ||
      key === 'object' ||
      key === 'type' ||
      key === 'required'
    ) {
      console.log(`Skipping internal/invalid field: ${key}`);
      return;
    }

    console.log(`🔍 Processando campo: ${key}`, schema);

    const field: DynamicField = {
      key,
      label: schema.title || key,
      type: schema.type || 'string',
      required: schema.required || false,
      description: schema.description || '',
      enum: schema.enum || null,
      default: schema.default || null,
      placeholder: schema.placeholder || '',
    };

    console.log(`✅ Campo gerado:`, field);
    fields.push(field);
  });

  console.log('📋 Campos dinâmicos gerados:', fields);
  return fields;
};

// Função para gerar texto descritivo das ações
export const generateActionText = (actions: FormAction[]): string => {
  if (actions.length === 0) {
    return 'Nenhuma Ação';
  }

  if (actions.length === 1) {
    const action = actions[0];
    return action.actionLabel || 'Ação configurada';
  }

  const actionTexts = actions.map(action => action.actionLabel || 'Ação configurada');

  if (actions.length === 2) {
    return `${actionTexts[0]} e ${actionTexts[1]}`;
  }

  const lastAction = actionTexts.pop();
  return `${actionTexts.join(', ')} e ${lastAction}`;
};

// Função para filtrar ações baseada nos filtros de busca
export const filterActions = (
  actionDefinitions: ActionDefinition[],
  filters: ActionSearchFilters
): ActionDefinition[] => {
  if (!filters.searchTerm.trim()) {
    return actionDefinitions;
  }

  const searchTerm = filters.searchTerm.toLowerCase();

  return actionDefinitions.filter(actionDef => {
    const matchesSearch =
      actionDef.name.toLowerCase().includes(searchTerm) ||
      actionDef.description?.toLowerCase().includes(searchTerm) ||
      actionDef.action_type.toLowerCase().includes(searchTerm) ||
      actionDef.object_type.toLowerCase().includes(searchTerm);

    const matchesType = !filters.actionType || actionDef.action_type === filters.actionType;
    const matchesObject = !filters.objectType || actionDef.object_type === filters.objectType;

    return matchesSearch && matchesType && matchesObject;
  });
};

// Função para converter ações do frontend para o formato da API
export const convertToApiActions = (
  actions: FormAction[],
  ruleId: string,
  actionDefinitions: ActionDefinition[] = []
): CreateAutomationActionRequest[] => {
  console.log('=== DEBUG: convertToApiActions ===');
  console.log('Ações para converter:', actions);
  console.log('Rule ID:', ruleId);
  console.log('Action Definitions:', actionDefinitions);

  const numericRuleId = parseInt(ruleId, 10);
  console.log('🔍 Numeric Rule ID:', numericRuleId);

  if (isNaN(numericRuleId)) {
    console.error('❌ Rule ID inválido:', ruleId);
    throw new Error(`Rule ID inválido: ${ruleId}`);
  }

  return actions.map((action, index) => {
    console.log(`🔍 Processando ação ${index + 1}:`, action);

    // Encontrar a definição da ação para obter o object_type
    const actionDef = actionDefinitions.find(def => def.id.toString() === action.actionKey);
    console.log('🔍 Action Definition encontrada:', actionDef);

    if (!action.actionKey) {
      console.error(`❌ Ação ${index + 1} sem actionKey:`, action);
      throw new Error(`Ação ${index + 1} sem actionKey`);
    }

    const actionDefId = parseInt(action.actionKey, 10);
    if (isNaN(actionDefId)) {
      console.error(`❌ Action Key inválido para ação ${index + 1}:`, action.actionKey);
      throw new Error(`Action Key inválido: ${action.actionKey}`);
    }

    const apiAction: CreateAutomationActionRequest = {
      rule_id: numericRuleId,
      action_def_id: actionDefId,
      config: {
        // ✅ Incluir configurações dinâmicas (team_name, team_uuid, auto_create, etc)
        ...(action.dynamicFields && { ...action.dynamicFields }),
      },
      order: action.order || index + 1,
    };

    console.log(`✅ Ação ${index + 1} convertida para API:`, apiAction);
    console.log(`   - config:`, apiAction.config);
    return apiAction;
  });
};

// Função para converter ações da API para o formato do frontend
export const convertFromApiActions = (
  apiActions: AutomationAction[],
  actionDefinitions: ActionDefinition[]
): FormAction[] => {
  console.log('=== DEBUG: convertFromApiActions ===');
  console.log('API Actions:', apiActions);
  console.log('Action Definitions:', actionDefinitions);

  return apiActions.map(apiAction => {
    // Usar action_definition que vem junto com a ação
    const actionDef = apiAction.action_definition;

    const formAction: FormAction = {
      id: apiAction.uuid,
      actionKey: actionDef.id.toString(),
      actionLabel: actionDef.name || 'Ação desconhecida',
      config: apiAction.config,
      dynamicFields: { ...apiAction.config },
    };

    console.log(`✅ Ação convertida da API:`, formAction);
    return formAction;
  });
};

// Função para criar ações na API
export const createActionsInApi = async (
  actions: FormAction[],
  ruleId: string,
  createActionFunction: (data: CreateAutomationActionRequest) => Promise<AutomationAction>,
  actionDefinitions: ActionDefinition[] = []
): Promise<boolean> => {
  console.log('=== DEBUG: createActionsInApi ===');
  console.log('Ações para criar:', actions);
  console.log('Rule ID:', ruleId);

  try {
    const apiActions = convertToApiActions(actions, ruleId, actionDefinitions);
    console.log('Ações convertidas para API:', apiActions);

    const createdActions: AutomationAction[] = [];

    for (const apiAction of apiActions) {
      console.log('Criando ação na API:', apiAction);
      const createdAction = await createActionFunction(apiAction);
      createdActions.push(createdAction);
      console.log('Ação criada com sucesso:', createdAction);
    }

    console.log('✅ Todas as ações foram criadas com sucesso:', createdActions);
    return true;
  } catch (error) {
    console.error('❌ Erro ao criar ações na API:', error);
    return false;
  }
};

// Função para atualizar ações na API
export const updateActionsInApi = async (
  actions: FormAction[],
  ruleId: string,
  updateActionFunction: (
    uuid: string,
    data: Partial<CreateAutomationActionRequest>
  ) => Promise<AutomationAction>,
  actionDefinitions: ActionDefinition[] = []
): Promise<boolean> => {
  console.log('=== DEBUG: updateActionsInApi ===');
  console.log('Ações para atualizar:', actions);
  console.log('Rule ID:', ruleId);

  try {
    const apiActions = convertToApiActions(actions, ruleId, actionDefinitions);
    console.log('Ações convertidas para API:', apiActions);

    const updatedActions: AutomationAction[] = [];

    for (const apiAction of apiActions) {
      const formAction = actions.find(a => a.actionKey === apiAction.action_def_id.toString());

      if (formAction?.id) {
        console.log('Atualizando ação na API:', formAction.id, apiAction);
        const updatedAction = await updateActionFunction(formAction.id, apiAction);
        updatedActions.push(updatedAction);
        console.log('Ação atualizada com sucesso:', updatedAction);
      }
    }

    console.log('✅ Todas as ações foram atualizadas com sucesso:', updatedActions);
    return true;
  } catch (error) {
    console.error('❌ Erro ao atualizar ações na API:', error);
    return false;
  }
};

// Função para deletar ações na API
export const deleteActionsInApi = async (
  actions: FormAction[],
  ruleId: string,
  deleteActionFunction: (uuid: string) => Promise<void>
): Promise<boolean> => {
  console.log('=== DEBUG: deleteActionsInApi ===');
  console.log('Ações para deletar:', actions);
  console.log('Rule ID:', ruleId);

  try {
    const deletePromises = actions
      .filter(action => action.id) // Apenas ações que existem na API
      .map(action => deleteActionFunction(action.id!));

    await Promise.all(deletePromises);
    console.log('✅ Todas as ações foram deletadas com sucesso');
    return true;
  } catch (error) {
    console.error('❌ Erro ao deletar ações na API:', error);
    return false;
  }
};

// Função para truncar texto
export const truncateText = (text: string, maxLength: number = 40): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Função para obter ícone da ação baseado no tipo
export const getActionIcon = (actionType: string): string => {
  const iconMap: Record<string, string> = {
    add_user_to_team: '👥',
    create_team: '🏗️',
    send_email: '📧',
    send_notification: '🔔',
    create_event: '📅',
    add_tag: '🏷️',
    remove_tag: '❌',
    update_profile: '👤',
    default: '⚡',
  };

  return iconMap[actionType] || iconMap.default;
};

// Função para obter cor da ação baseado no tipo
export const getActionColor = (actionType: string): string => {
  const colorMap: Record<string, string> = {
    add_user_to_team: '#4CAF50',
    create_team: '#2196F3',
    send_email: '#FF9800',
    send_notification: '#9C27B0',
    create_event: '#F44336',
    add_tag: '#607D8B',
    remove_tag: '#E91E63',
    update_profile: '#795548',
    default: '#757575',
  };

  return colorMap[actionType] || colorMap.default;
};
